import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { from, Observable, switchMap, throwError } from "rxjs";
import { inject, Injectable } from "@angular/core";
import { catchError, map } from "rxjs/operators";
import { ErrorModalComponent } from "./error-modal/error-modal.component";
import { OAuthService } from "angular-oauth2-oidc";
import { QuangAuthService } from "quang/auth";
import { QuangModalService } from "quang/overlay/modal";
import { QuangToastService } from "quang/overlay/toast";
import { environment } from "src/environments/environment";
import { ErrorResponseDTO } from "../apiModule";
import { AuthControllerService } from "../store/auth-controller.service";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly quangModalService = inject(QuangModalService);
  private readonly oauthService = inject(OAuthService);
  private readonly authService = inject(QuangAuthService);
  private readonly authController = inject(AuthControllerService);
  private readonly quangToast = inject(QuangToastService);

  async extractErrorBody(error: any): Promise<ErrorResponseDTO> {
    if (!(error?.error instanceof Blob)) {
      return error?.error;
    }
    let text: string;
    try {
      text = await error.error.text();
    } catch {
      return { message: error?.statusText };
    }
    try {
      const parsed = JSON.parse(text);
      return typeof parsed === "object" && parsed !== null
        ? parsed
        : { message: text };
    } catch {
      return { message: text || error?.statusText };
    }
  }

  triggerLogin() {
    this.oauthService.logOut(true);
    this.oauthService.stopAutomaticRefresh();
    window.location.reload();
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    return next.handle(req).pipe(
      map((resp) => resp),
      catchError((error) => {
        const isRevoke = req.url.includes("openid-connect/revoke");
        const isToken = req.url.includes("openid-connect/token");

        if (!isRevoke && !isToken) {
          return from(this.extractErrorBody(error)).pipe(
            switchMap((body) => this.handleApiError(error, body)),
          );
        }

        if (!isRevoke) {
          this.handleTokenError();
        } else {
          this.triggerLogin();
        }
        return throwError(new HttpErrorResponse(error));
      }),
    );
  }

  private handleApiError(
    error: any,
    body: ErrorResponseDTO,
  ): Observable<never> {
    let targetError: ErrorResponseDTO = body;
    if (error?.status === 401) {
      targetError = { message: error?.statusText };
      this.triggerLogin();
    } else if (error?.status === 403) {
      targetError = { message: error?.statusText };
    }
    if (!targetError?.message) {
      this.quangToast.openToast({
        type: "error",
        title: "Errore",
        position: "top-right",
        text: "Si è verificato un errore imprevisto.",
        timing: 5000,
        showCloseButton: true,
      });
    } else {
      this.quangModalService.showModal(
        ErrorModalComponent,
        { position: "center", width: "480px", height: "240px" },
        { error: targetError },
      );
    }
    return throwError(new HttpErrorResponse(error));
  }

  private handleTokenError(): void {
    if (!environment.isApp) {
      this.authService.logout();
      return;
    }
    this.oauthService.revocationEndpoint = `${environment.domain}/auth/realms/quake/protocol/openid-connect/revoke`;
    this.authController.clearAuth();
    try {
      this.oauthService
        .revokeTokenAndLogout(null, true)
        .then(() => this.triggerLogin())
        .catch(() => this.triggerLogin());
    } catch {
      this.triggerLogin();
    }
  }
}
